import { getMetrikaCounterIdFromEnv } from "./metrikaCounterId";
import { buildMetrikaPurchase } from "./metrikaPurchase";
import { claimEcommerceSendForPayment, ECOMMERCE_CLAIM_TTL_MS, hasAlreadySentEcommerceForPayment, markEcommerceSentForPayment, PENDING_PAYMENT_STORAGE_KEY, } from "./paymentAnalytics";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
/**
 * Отправка в Яндекс.Метрику. Идентификаторы целей — ТОЛЬКО из реестра
 * `metrikaGoals.ts` (там же — какие цели шлёт браузер, а какие бэкенд);
 * `ymReachGoal` типизирован и серверную/неизвестную цель не примет.
 *
 * Воронка сайта (по образцу VPN, но со своим продуктовым шагом «песня»):
 *   cta_click → auth_* → text_gen_screen_open → text_gen_start →
 *   song_gen_start → checkout_open → checkout_submit → payment_return →
 *   payment_success
 */
// Тег Метрики грузится асинхронно (см. yandexMetrikaTagLoader), поэтому ранняя
// цель — например `checkout_open` при ПРЯМОМ заходе на пейвол — легко
// опережает момент, когда `window.ym` определён, и молча теряется. Ждём
// готовности короткими опросами с жёстким дедлайном.
const YM_READY_TIMEOUT_MS = 2000;
const YM_READY_POLL_MS = 50;
/**
 * Зовёт `fire`, как только доступен `window.ym`. Быстрый путь — тег уже
 * поднялся (обычный случай для кликов); иначе опрашиваем `window.ym` раз в
 * YM_READY_POLL_MS до дедлайна YM_READY_TIMEOUT_MS и срабатываем ровно один
 * раз: что первым, появление ym или таймаут (флаг `settled`). По таймауту тихо
 * сдаёмся — трекинг best-effort и не должен ничего ломать.
 */
function whenYmReady(fire) {
    if (typeof window === "undefined") {
        return;
    }
    const ymNow = window.ym;
    if (typeof ymNow === "function") {
        fire(ymNow);
        return;
    }
    let settled = false;
    const poll = window.setInterval(() => {
        if (settled) {
            return;
        }
        const ym = window.ym;
        if (typeof ym === "function") {
            settled = true;
            window.clearInterval(poll);
            window.clearTimeout(deadline);
            fire(ym);
        }
    }, YM_READY_POLL_MS);
    const deadline = window.setTimeout(() => {
        if (settled) {
            return;
        }
        settled = true;
        window.clearInterval(poll);
    }, YM_READY_TIMEOUT_MS);
}
function callYm(method, ...args) {
    if (typeof window === "undefined") {
        return;
    }
    const id = getMetrikaCounterIdFromEnv();
    if (id === null) {
        return;
    }
    whenYmReady((ym) => {
        try {
            ym(id, method, ...args);
        }
        catch {
            /* синхронное исключение из ym(...) — best-effort, молчим */
        }
    });
}
/**
 * Виртуальный хит для SPA: корректные URL в отчётах Метрики.
 */
export function ymHitPage(urlPath, title) {
    const path = urlPath.startsWith("/") ? urlPath : `/${urlPath}`;
    callYm("hit", path, {
        title: title || (typeof document !== "undefined" ? document.title : ""),
    });
}
/**
 * Цель Метрики. Best-effort: ждёт готовности тега (см. whenYmReady), глушит
 * исключения и НИКОГДА не бросает наружу — трекинг не должен ломать ни клик,
 * ни навигацию. Имя цели — только из реестра (`MetrikaSiteGoal`).
 */
export function ymReachGoal(goalId, params, options) {
    const hasParams = Boolean(params && Object.keys(params).length > 0);
    const onSent = options?.onSent;
    if (onSent) {
        // tag.js может дёрнуть callback повторно (ретрай отправки) — нам важен
        // только первый.
        let fired = false;
        const callback = () => {
            if (fired) {
                return;
            }
            fired = true;
            onSent();
        };
        callYm("reachGoal", goalId, hasParams ? params : undefined, callback);
    }
    else if (hasParams) {
        callYm("reachGoal", goalId, params);
    }
    else {
        callYm("reachGoal", goalId);
    }
}
// Цели, отправленные за эту загрузку страницы (см. ymReachGoalOnce). Переменная
// уровня модуля, а не storage: воронка Метрики считает ВИЗИТЫ, поэтому «один
// раз за загрузку» — правильная гранулярность, и на новой загрузке цель обязана
// уйти снова.
const goalsSentOnce = new Set();
/**
 * Цель, отправляемая не чаще одного раза за загрузку страницы.
 *
 * Нужна ступеням, которые в SPA легко продублировать: `checkout_open` иначе
 * уходил бы на каждый ререндер эффекта, на открытие модалки пополнения поверх
 * уже открытого пейвола и на возврат по «назад».
 *
 * Ключ дедупа — цель + разрезы воронки (`placement`, `mode`): раньше ключом
 * было только имя цели, и `checkout_open` с модалки пополнения поверх пейвола
 * терял свой placement, а шаги создания песни — переключение classic ↔ chat.
 */
export function ymReachGoalOnce(goalId, params) {
    const key = [goalId, params?.placement, params?.mode]
        .filter((part) => part != null)
        .map(String)
        .join(":");
    if (goalsSentOnce.has(key)) {
        return;
    }
    goalsSentOnce.add(key);
    ymReachGoal(goalId, params);
}
// Дедуп setUserID — на ЗАГРУЗКУ СТРАНИЦЫ, а не на браузер: Метрика связывает
// собственный идентификатор с ClientID только в тех визитах, во время которых
// setUserID был вызван, поэтому маркер в storage заглушил бы связку во всех
// последующих визитах. Переменная модуля переживает StrictMode и повторные
// вызовы /me за одну загрузку, но сбрасывается на полной перезагрузке. Храним
// само значение, а не флаг: смена аккаунта в той же вкладке (logout → вход
// другим) отправит связку заново.
let lastYmUserIdSent = null;
/**
 * Привязка нашего id пользователя к его ClientID (`setUserID`).
 *
 * Нужна офлайн-конверсиям Метрики: бэкенд грузит оплаты по колонке `UserId` со
 * значением `str(user.id)`, и Метрика сматчит их только с теми визиторами, в
 * чьих визитах сайт вызывал setUserID.
 */
export function setYmUserId(userId) {
    const value = String(userId).trim();
    if (!value) {
        return;
    }
    if (value === lastYmUserIdSent) {
        return;
    }
    // Помечаем отправленным сразу: иначе повторные вызовы /me за ту же загрузку
    // наплодили бы параллельных ожидающих опросов whenYmReady.
    lastYmUserIdSent = value;
    callYm("setUserID", value);
}
// Дедуп userParams на загрузку страницы: /users/me зовётся часто, а слать
// одни и те же параметры посетителя на каждый вызов незачем.
let lastYmUserParamsSent = null;
/**
 * Параметры посетителя (`userParams`, отчёт «Параметры посетителей»): живут на
 * ClientID дольше визита — поэтому сюда идут устойчивые признаки человека
 * (A/B-варианты, `has_paid`), а не свойства конкретного визита. Пустые
 * значения (null/undefined/"") не шлём.
 */
export function setYmUserParams(params) {
    const clean = {};
    for (const [key, value] of Object.entries(params)) {
        if (value !== null && value !== undefined && value !== "") {
            clean[key] = value;
        }
    }
    if (Object.keys(clean).length === 0) {
        return;
    }
    const serialized = JSON.stringify(clean);
    if (serialized === lastYmUserParamsSent) {
        return;
    }
    lastYmUserParamsSent = serialized;
    callYm("userParams", clean);
}
let hasPaidReported = false;
/**
 * Отметить посетителя как покупателя: `has_paid: true` — параметром
 * посетителя (сегмент «покупатели» для исключения из ретаргетинга и
 * look-alike, как в Worldpay) и параметром визита (видно, в каком визите он
 * уже был покупателем). Раз за загрузку страницы.
 */
export function reportYmHasPaid() {
    if (hasPaidReported) {
        return;
    }
    hasPaidReported = true;
    callYm("userParams", { has_paid: true });
    callYm("params", { has_paid: true });
}
/**
 * Электронная торговля Метрики (контейнер dataLayer).
 * @see https://yandex.ru/support/metrica/ru/ecommerce/data
 */
export function pushMetrikaPurchase(payload) {
    if (typeof window === "undefined") {
        return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
        ecommerce: {
            currencyCode: payload.currencyCode || "RUB",
            purchase: {
                actionField: {
                    id: payload.transactionId,
                    revenue: payload.revenue,
                },
                products: payload.products.map((p) => ({
                    id: p.id,
                    name: p.name,
                    price: p.price,
                    quantity: p.quantity,
                    ...(p.category ? { category: p.category } : {}),
                })),
            },
        },
    });
}
export function rememberPendingPaymentForAnalytics(paymentDbId) {
    const v = String(paymentDbId);
    try {
        sessionStorage.setItem(PENDING_PAYMENT_STORAGE_KEY, v);
    }
    catch {
        /* ignore */
    }
    try {
        localStorage.setItem(PENDING_PAYMENT_STORAGE_KEY, v);
    }
    catch {
        /* ignore */
    }
}
export function readPendingPaymentIdFromStorage() {
    let raw = null;
    try {
        raw = sessionStorage.getItem(PENDING_PAYMENT_STORAGE_KEY);
    }
    catch {
        /* ignore */
    }
    if (!raw) {
        try {
            raw = localStorage.getItem(PENDING_PAYMENT_STORAGE_KEY);
        }
        catch {
            /* ignore */
        }
    }
    if (!raw || !/^\d+$/.test(raw)) {
        return null;
    }
    return Number(raw);
}
export function clearPendingPaymentFromStorage() {
    try {
        sessionStorage.removeItem(PENDING_PAYMENT_STORAGE_KEY);
    }
    catch {
        /* ignore */
    }
    try {
        localStorage.removeItem(PENDING_PAYMENT_STORAGE_KEY);
    }
    catch {
        /* ignore */
    }
}
/**
 * Сколько вызывающий максимум ждёт подтверждения отправки покупки (вкладка
 * `/payment/success` закрывается только после него). Дольше не держим:
 * пользователь уже видит «Оплата прошла».
 */
export const PURCHASE_SENT_WAIT_MS = 4000;
/**
 * Пауза после push ecommerce до резолва: tag.js забирает `dataLayer` по
 * подписке на push и отправляет асинхронно — даём ему уйти до закрытия вкладки.
 */
const ECOMMERCE_FLUSH_MS = 500;
/** Максимальный случайный разнос перепроверки чужого claim без Web Locks. */
export const PURCHASE_RECHECK_JITTER_MS = 3000;
// Платежи, для которых уже запланирована перепроверка чужого claim (см. ниже),
// — чтобы не плодить таймеры на повторные сигналы.
const purchaseRecheckScheduled = new Set();
function getWebLocks() {
    try {
        const locks = window.navigator?.locks;
        return locks && typeof locks.request === "function" ? locks : null;
    }
    catch {
        return null;
    }
}
/**
 * Захват claim — атомарно между вкладками через Web Locks, где они есть:
 * localStorage сам по себе не атомарен, и две вкладки могли одновременно
 * прочитать «свободно» и обе отправить покупку. Без Web Locks (старые
 * браузеры) — как есть; гонку перепроверок разносит джиттер.
 */
function claimPurchase(paymentId) {
    const locks = getWebLocks();
    if (!locks) {
        return Promise.resolve(claimEcommerceSendForPayment(paymentId));
    }
    return locks
        .request(`virtuoz_ecommerce_${paymentId}`, () => claimEcommerceSendForPayment(paymentId))
        .catch(() => claimEcommerceSendForPayment(paymentId));
}
/**
 * Успешная оплата в Метрику: цель `payment_success` + ecommerce-покупка. Ровно
 * один раз на платёж.
 *
 * Идемпотентность обязана быть своей: у Метрики нет дедупликации ни между
 * загрузками страницы, ни между вкладками (и по `actionField.id` покупки
 * документация дедуп не обещает — см. docs/metrika-goals.md), а возврат с
 * кассы приходит сразу по двум путям — вкладка `/payment/success` и сигнал в
 * остальные вкладки приложения (см. paymentReturn).
 *
 * Как устроено (риск R7 из metrika_comparison.md: раньше постоянный маркер
 * ставился ДО отправки, и цель терялась навсегда, если вкладка закрывалась
 * раньше, чем tag.js успевал её отправить):
 * 1. Занимаем короткий claim на все вкладки (TTL ECOMMERCE_CLAIM_TTL_MS),
 *    атомарно под Web Lock (см. claimPurchase).
 * 2. Шлём цель; постоянный маркер «отправлено» ставим только в её callback,
 *    т.е. после фактической отправки. Не дождались (tag.js заблокирован,
 *    вкладку закрыли) — маркера нет, claim истечёт, отправит следующая попытка.
 * 3. Ecommerce пушим ТОЛЬКО в том же callback, сразу после маркера: ретрай
 *    бывает лишь когда маркера нет, а значит, и ecommerce не пушился — выручка
 *    не задвоится. Цена: закрыли вкладку в эти ~0,5 с — теряется ecommerce, но
 *    не цель и не доход дважды.
 * 4. Claim держит другая вкладка — перепроверяем после его истечения (+ джиттер
 *    без Web Locks): та вкладка могла закрыться, так ничего и не отправив.
 *
 * Возвращает промис: резолвится после отправки, по таймауту
 * PURCHASE_SENT_WAIT_MS или сразу, если отправлять нечего. Не reject'ится.
 */
export function reportMetrikaPurchaseOnce(payment) {
    // Факт оплаты известен — отмечаем покупателя, даже если саму цель уже
    // отправила другая вкладка или прошлая загрузка.
    reportYmHasPaid();
    // Метрика не сконфигурирована — callback не придёт никогда, и держать
    // вызывающего (закрытие вкладки) незачем.
    if (typeof window === "undefined" || getMetrikaCounterIdFromEnv() === null) {
        return Promise.resolve();
    }
    return claimPurchase(payment.id).then((claimed) => {
        if (claimed) {
            return sendPurchase(payment);
        }
        schedulePurchaseRecheck(payment);
        return undefined;
    });
}
function schedulePurchaseRecheck(payment) {
    if (hasAlreadySentEcommerceForPayment(payment.id) ||
        purchaseRecheckScheduled.has(payment.id)) {
        return;
    }
    purchaseRecheckScheduled.add(payment.id);
    const jitter = getWebLocks() ? 0 : Math.random() * PURCHASE_RECHECK_JITTER_MS;
    window.setTimeout(() => {
        purchaseRecheckScheduled.delete(payment.id);
        void reportMetrikaPurchaseOnce(payment);
    }, ECOMMERCE_CLAIM_TTL_MS + 500 + jitter);
}
function sendPurchase(payment) {
    const purchase = buildMetrikaPurchase(payment);
    return new Promise((resolve) => {
        const timer = window.setTimeout(resolve, PURCHASE_SENT_WAIT_MS);
        ymReachGoal("payment_success", {
            payment_id: payment.id,
            amount_rub: purchase.revenue,
            ...(payment.package_id != null ? { package_id: payment.package_id } : {}),
        }, {
            onSent: () => {
                markEcommerceSentForPayment(payment.id);
                pushMetrikaPurchase(purchase);
                window.clearTimeout(timer);
                window.setTimeout(resolve, ECOMMERCE_FLUSH_MS);
            },
        });
    });
}
/**
 * После первого прогона (счётчик уже зафиксировал URL из document) шлём hit на смену маршрута.
 */
export function useYandexMetrikaPageHit() {
    const location = useLocation();
    const path = `${location.pathname}${location.search}`;
    const skipNextRef = useRef(true);
    useEffect(() => {
        if (skipNextRef.current) {
            skipNextRef.current = false;
            return;
        }
        ymHitPage(path);
    }, [path]);
}
